import type { NitroFetchRequest } from 'nitropack/types';
import type { HttpFetchOptions, HttpFetchContext } from './types';
import { getStorageSync } from '@/utils/storage.js';
import { getPersistentDeviceID } from '@/utils/utils';

/** 客户端 deviceId 只生成一次，避免并发请求重复跑指纹 */
let deviceIdPromise: Promise<string> | null = null;

function resolveClientDeviceId(): Promise<string> {
	if (!import.meta.client) return Promise.resolve('');
	if (!deviceIdPromise) {
		deviceIdPromise = getPersistentDeviceID();
	}
	return deviceIdPromise;
}

function normalizeIp(ip: string | null | undefined): string {
	const value = String(ip || '').trim();
	if (!value) return '';
	if (value === '::1') return '127.0.0.1';
	if (value.startsWith('::ffff:')) return value.slice(7);
	return value;
}

function resolveServerHttpClientIp(incoming: Record<string, string | undefined>, remoteAddress?: string): string {
	const forwardedFor = String(incoming['x-forwarded-for'] || '').trim();
	if (forwardedFor) {
		return normalizeIp(forwardedFor.split(',')[0]?.trim());
	}

	const realIp = String(incoming['x-real-ip'] || '').trim();
	if (realIp) return normalizeIp(realIp);

	return normalizeIp(remoteAddress);
}

async function handleErrorToast(message: string) {
	if (import.meta.server) return;
	const { showToast } = await import('vant');
	showToast({
		message: message,
		duration: 3000,
	});
}
// 拦截器会在 $fetch 的异步回调里执行，可能已脱离 Nuxt 请求上下文，不能直接调用 useRuntimeConfig/useCookie 等。
// 方案：插件入参拿到 nuxtApp，在拦截器里用 nuxtApp.runWithContext() 调用 composable；apiBase 在插件同步阶段取一次。
export default defineNuxtPlugin(nuxtApp => {
	const config = useRuntimeConfig();
	const defaultBaseURL = config.public.apiBase as string;

	function buildHeaders(headers: any): Headers {
		const xsrf = useCookie('XSRF-TOKEN');
		const authHeaders: Record<string, string> = {
			sourceType: 'mall',
			req: '1',
		};

		if (config.public.authGuard === 'web') {
			authHeaders['X-XSRF-TOKEN'] = xsrf.value ?? '';
		}

		let token: string | null = null;
		try {
			const auth = useAuthStore();
			token = auth.accessToken ?? null;
		} catch (_) {}
		if (!token) {
			try {
				token = getStorageSync('cmdc_access_token_mobile') as string | null;
			} catch (_) {}
		}
		if (token) {
			authHeaders['cmdc_access_token'] = String(token);
		}

		return {
			Accept: 'application/json',
			...headers,
			...authHeaders,
		};
	}

	function buildBaseURL(baseURL: string): string {
		if (baseURL) return baseURL;
		return defaultBaseURL;
	}

	function isRequestWithAuth(baseURL: string, path: string): boolean {
		return !baseURL && !path.startsWith('/_nuxt') && !path.startsWith('http://') && !path.startsWith('https://');
	}
	function getCookieValue(cookieStr: string | undefined, name: string): string | null {
		if (!cookieStr) return null;
		// Cookie 头格式: "a=1; b=2; c=3"，匹配 name=value（前面是开头或 "; "）
		const match = cookieStr.match(new RegExp('(?:^|;\\s*)' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
		return match ? decodeURIComponent(match[1].trim()) : null;
	}

	async function callHooks(context: any, hooks: any) {
		if (Array.isArray(hooks)) {
			for (const hook of hooks) {
				await hook(context);
			}
		} else if (hooks) {
			await hooks(context);
		}
	}

	const http = $fetch.create<unknown, NitroFetchRequest>(<HttpFetchOptions>{
		baseURL: '',
		retry: false,

		async onRequest(context: HttpFetchContext) {
			await callHooks(context, context.options.onFetch);
			if (!isRequestWithAuth(context.options.baseURL ?? '', context.request.toString())) return;
			context.options.credentials = 'include';
			context.options.baseURL = buildBaseURL(context.options.baseURL ?? '');
			// 在 runWithContext 里调 buildHeaders，避免在异步回调里直接调 useCookie/useAuthStore
			context.options.headers = (await nuxtApp.runWithContext(() => buildHeaders(context.options.headers))) as Headers & HeadersInit;

			if (import.meta.server) {
				const requestContext = await Promise.resolve(
					nuxtApp.runWithContext(() => {
						const headers = useRequestHeaders(['cookie', 'x-forwarded-for', 'x-real-ip']);
						const event = useRequestEvent();
						return {
							headers,
							remoteAddress: event?.node.req.socket?.remoteAddress,
						};
					})
				);
				const headers = requestContext?.headers || {};
				const cookieStr = typeof headers === 'object' && headers !== null && 'cookie' in headers ? headers.cookie : undefined;
				const token = getCookieValue(cookieStr, 'cmdc_access_token_mobile');
				const httpClientIp = resolveServerHttpClientIp(headers as Record<string, string | undefined>, requestContext?.remoteAddress);
				context.options.headers = {
					...context.options.headers,
					...headers,
					...(token ? { cmdc_access_token: token } : {}),
					...(httpClientIp ? { HTTP_CLIENT_IP: httpClientIp } : {}),
				};
			}
			if (import.meta.client) {
				const deviceId = await resolveClientDeviceId();
				if (deviceId) {
					context.options.headers = {
						...context.options.headers,
						deviceId,
					};
				}
			}
		},
		async onRequestError(context: HttpFetchContext) {
			await callHooks(context, context.options.onFetchError);

			if (context.error?.name === 'AbortError') return;

			handleErrorToast(context.error?.message ?? '系统错误，请联系管理员');
		},
		async onResponse(context: HttpFetchContext) {
			await callHooks(context, context.options.onFetchResponse);
			const data = context.response._data;
			// 接口返回未登录时，可在此观察 data.success / data.message
			if (data?.success == false) {
				handleErrorToast(data.message ?? '系统错误，请联系管理员');
			}
		},
		async onResponseError(context: HttpFetchContext) {
			await callHooks(context, context.options.onFetchResponseError);
			if (context.response.status === 401) {
				await nuxtApp.runWithContext(async () => {
					const auth = useAuthStore();
					auth.reset();
					// 已在登录页则不重复跳转
					const router = nuxtApp.$router as unknown as { currentRoute?: { value?: { path?: string } } } | undefined;
					if (router?.currentRoute?.value?.path === '/login') return;
					await navigateTo('/login', { replace: true });
				});
			} else if (context.response.status !== 200) {
				const msg = context.response._data?.message ?? context.response.statusText ?? '系统错误，请联系管理员';
				handleErrorToast(msg);
			}
		},
	});
	setHttp(http);
	return {
		provide: {
			http,
		},
	};
});
